<template>
  <v-card>
    <v-card-title class="d-flex align-center" v-if="!hideTitle">
      <v-icon left>mdi-calendar-month</v-icon>
      <h5 class="font-weight-bold text-h5 text-typo mb-0">本月打卡紀錄</h5>
      <v-spacer />
      <v-progress-circular
        v-if="loading"
        indeterminate
        color="primary"
        size="24"
      />
    </v-card-title>

    <v-card-text>
      <v-row>
        <!-- Year/Month Selection -->
        <v-col cols="12" md="4" class="d-flex align-center">
          <v-select
            v-model="selectedYear"
            :items="yearOptions"
            label="年份"
            dense
            outlined
            hide-details
            class="mr-2"
            @change="onDateChange"
            style="max-width: 120px"
          ></v-select>
          <v-select
            v-model="selectedMonth"
            :items="monthOptions"
            label="月份"
            dense
            outlined
            hide-details
            @change="onDateChange"
            style="max-width: 100px"
          ></v-select>
        </v-col>

        <!-- User Selection -->
        <v-col cols="12" md="4" v-if="!hideUserSelect">
          <v-autocomplete
            v-model="selectedUserId"
            :items="users"
            :loading="loadingUsers"
            item-text="name"
            item-value="id"
            placeholder="請輸入姓名"
            dense
            outlined
            hide-details
            prepend-inner-icon="mdi-magnify"
            clearable
            @change="onUserChange"
          >
            <template v-slot:label>
              <span class="red--text">搜尋員工</span>
            </template>
            <template v-slot:item="{ item }">
              <v-list-item-content>
                <v-list-item-title>
                  {{ item.name }}
                </v-list-item-title>
                <v-list-item-subtitle>
                  {{ item.job_title }}
                </v-list-item-subtitle>
              </v-list-item-content>
            </template>
          </v-autocomplete>
        </v-col>

        <!-- Legend / Info (Optional) -->
        <v-col cols="12" md="4">
          <!-- Placeholder for future actions -->
        </v-col>
      </v-row>
    </v-card-text>

    <!-- Upload Dialog (Reused/Copied logic if needed, but maybe not for this view yet?) -->
    <!-- The user didn't explicitly ask for upload here, but Daily has it. Leaving it out or keeping simpler for now based on request "item 2 put Monthly Clock Records" -->

    <v-data-table
      :headers="headers"
      :items="records"
      :loading="loading"
      :server-items-length="totalItems"
      :items-per-page="itemsPerPage"
      class="table"
      hide-default-footer
      disable-pagination
      @page-count="pageCount = $event"
      @update:items-per-page="handleItemsPerPageChange"
    >
      <template v-slot:progress>
        <v-progress-linear indeterminate color="primary" />
      </template>

      <!-- Custom Date Column -->
      <template v-slot:item.date="{ item }">
        <div class="d-flex align-center">
          <span class="text-sm font-weight-bold text-body">
            <!-- {{ getRecordDate(item) }} -->
            {{ item.clock_date }}
          </span>
          <span class="text-xs text-secondary ml-1">
            {{ getRecordWeekDay(item) }}
          </span>
        </div>
      </template>

      <template v-slot:item.clock_in_actual="{ item }">
        <div v-if="editedItem && editedItem.id === item.id">
          <date-time-picker
            :value="formatDateTime(editedItem.clock_in_actual)"
            :date.sync="clockInActualDate"
            :time.sync="clockInActualTime"
            @confirm="updateClockInActual"
          />
        </div>
        <div v-else class="d-flex align-center">
          <v-menu
            v-if="isValidGPS(item.gps_clock_in)"
            :value="activeGPSKey === item.id + '_in'"
            @input="(val) => (activeGPSKey = val ? item.id + '_in' : null)"
            offset-y
            :close-on-content-click="false"
            min-width="250"
          >
            <template v-slot:activator="{ on, attrs }">
              <span
                v-bind="attrs"
                v-on="on"
                class="primary--text font-weight-bold text-sm"
                style="cursor: pointer; border-bottom: 1px dashed"
              >
                {{ formatDateTime(item.clock_in_actual) }}
              </span>
            </template>
            <v-card class="card-shadow border-radius-xl">
              <v-card-title
                class="pa-2 d-flex justify-space-between align-center"
              >
                <span class="text-subtitle-2 ml-2">GPS 位置 (上班)</span>
                <v-btn icon x-small @click="activeGPSKey = null">
                  <v-icon size="16">mdi-close</v-icon>
                </v-btn>
              </v-card-title>
              <v-divider />
              <v-card-text class="pa-3">
                <div class="mb-3 text-body-2 font-weight-bold">
                  {{ item.gps_clock_in }}
                </div>
                <v-btn
                  block
                  small
                  color="primary"
                  class="bg-gradient-primary"
                  @click="openGPSInGoogleMaps(item.gps_clock_in)"
                >
                  <v-icon left size="16">mdi-map-marker</v-icon>
                  開啟地圖
                </v-btn>
              </v-card-text>
            </v-card>
          </v-menu>
          <span v-else class="text-sm font-weight-normal text-body">
            {{ formatDateTime(item.clock_in_actual) }}
          </span>
        </div>
      </template>

      <template v-slot:item.clock_out_actual="{ item }">
        <div v-if="editedItem && editedItem.id === item.id">
          <date-time-picker
            :value="formatDateTime(editedItem.clock_out_actual)"
            :date.sync="clockOutActualDate"
            :time.sync="clockOutActualTime"
            @confirm="updateClockOutActual"
          />
        </div>
        <div v-else class="d-flex align-center">
          <v-menu
            v-if="isValidGPS(item.gps_clock_out)"
            :value="activeGPSKey === item.id + '_out'"
            @input="(val) => (activeGPSKey = val ? item.id + '_out' : null)"
            offset-y
            :close-on-content-click="false"
            min-width="250"
          >
            <template v-slot:activator="{ on, attrs }">
              <span
                v-bind="attrs"
                v-on="on"
                class="primary--text font-weight-bold text-sm"
                style="cursor: pointer; border-bottom: 1px dashed"
              >
                {{ formatDateTime(item.clock_out_actual) }}
              </span>
            </template>
            <v-card class="card-shadow border-radius-xl">
              <v-card-title
                class="pa-2 d-flex justify-space-between align-center"
              >
                <span class="text-subtitle-2 ml-2">GPS 位置 (下班)</span>
                <v-btn icon x-small @click="activeGPSKey = null">
                  <v-icon size="16">mdi-close</v-icon>
                </v-btn>
              </v-card-title>
              <v-divider />
              <v-card-text class="pa-3">
                <div class="mb-3 text-body-2 font-weight-bold">
                  {{ item.gps_clock_out }}
                </div>
                <v-btn
                  block
                  small
                  color="primary"
                  class="bg-gradient-primary"
                  @click="openGPSInGoogleMaps(item.gps_clock_out)"
                >
                  <v-icon left size="16">mdi-map-marker</v-icon>
                  開啟地圖
                </v-btn>
              </v-card-text>
            </v-card>
          </v-menu>
          <span v-else class="text-sm font-weight-normal text-body">
            {{ formatDateTime(item.clock_out_actual) }}
          </span>
        </div>
      </template>

      <template v-slot:item.lunch_break="{ item }">
        <div v-if="editedItem && editedItem.id === item.id">
          <v-checkbox
            v-model="editedItem.lunch_break"
            dense
            hide-details
          ></v-checkbox>
        </div>
        <div v-else class="d-flex align-center">
          <span class="text-sm font-weight-normal text-body">
            {{ item.lunch_break ? "true" : "false" }}
          </span>
        </div>
      </template>
      <template v-slot:item.lunch_break_start_actual="{ item }">
        <div v-if="editedItem && editedItem.id === item.id">
          <date-time-picker
            :value="formatDateTime(editedItem.lunch_break_start_actual)"
            :date.sync="lunchBreakStartActualDate"
            :time.sync="lunchBreakStartActualTime"
            @confirm="updateLunchBreakStartActual"
          />
        </div>
        <div v-else class="d-flex align-center">
          <span class="text-sm font-weight-normal text-body">
            {{ formatDateTime(item.lunch_break_start_actual) }}
          </span>
        </div>
      </template>
      <template v-slot:item.lunch_break_end_actual="{ item }">
        <div v-if="editedItem && editedItem.id === item.id">
          <date-time-picker
            :value="formatDateTime(editedItem.lunch_break_end_actual)"
            :date.sync="lunchBreakEndActualDate"
            :time.sync="lunchBreakEndActualTime"
            @confirm="updateLunchBreakEndActual"
          />
        </div>
        <div v-else class="d-flex align-center">
          <span class="text-sm font-weight-normal text-body">
            {{ formatDateTime(item.lunch_break_end_actual) }}
          </span>
        </div>
      </template>

      <template v-slot:item.overtime="{ item }">
        <div v-if="editedItem && editedItem.id === item.id">
          <v-select
            v-model="editedItem.overtime"
            dense
            hide-details
            :items="[
              { text: '-', value: null },
              { text: '是', value: true },
              { text: '否', value: false },
            ]"
          ></v-select>
        </div>
        <div v-else class="d-flex align-center">
          <span class="text-sm font-weight-normal text-body">
            {{
              item.overtime !== null && item.overtime !== undefined
                ? item.overtime
                  ? "是"
                  : "否"
                : "-"
            }}
          </span>
        </div>
      </template>
      <template v-slot:item.overtime_record_id="{ item }">
        <div v-if="editedItem && editedItem.id === item.id">
          <v-text-field
            v-model.number="editedItem.overtime_record_id"
            dense
            hide-details
            type="number"
          ></v-text-field>
        </div>
        <div v-else class="d-flex align-center">
          <v-tooltip bottom v-if="item.overtimeRecord">
            <template v-slot:activator="{ on, attrs }">
              <span
                v-bind="attrs"
                v-on="on"
                class="text-sm font-weight-normal text-body"
                style="cursor: pointer; border-bottom: 1px dashed #ccc"
              >
                {{ item.overtimeRecord.overtime_minutes }}分鐘
              </span>
            </template>
            <span>
              {{ formatDateTime(item.overtimeRecord.start_time) }} ~
              {{ formatDateTime(item.overtimeRecord.end_time) }}
            </span>
          </v-tooltip>
          <span v-else class="text-sm font-weight-normal text-body">
            {{
              item.overtime_record_id !== null &&
              item.overtime_record_id !== undefined
                ? item.overtime_record_id
                : "-"
            }}
          </span>
        </div>
      </template>
      <template v-slot:item.leave_record_ids="{ item }">
        <div
          class="d-flex flex-column"
          v-if="item.leave_records && item.leave_records.length > 0"
        >
          <div
            v-for="leave in item.leave_records"
            :key="leave.id"
            class="text-sm"
          >
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <span
                  v-bind="attrs"
                  v-on="on"
                  style="cursor: pointer; border-bottom: 1px dashed #ccc"
                >
                  <span class="text-body font-weight-bold">
                    {{ getLeaveTypeName(leave.leave_type) }}
                  </span>
                  <span class="text-secondary ml-1">
                    {{ leave.leave_minutes }}分
                  </span>
                </span>
              </template>
              <span>
                {{ formatDateTime(leave.start_time) }} ~
                {{ formatDateTime(leave.end_time) }}
              </span>
            </v-tooltip>
          </div>
        </div>
        <div v-else class="d-flex align-center">
          <span class="text-sm font-weight-normal text-body">
            {{
              item.leave_record_ids !== null &&
              item.leave_record_ids !== undefined
                ? item.leave_record_ids
                : "-"
            }}
          </span>
        </div>
      </template>

      <!-- extra -->
      <template v-slot:item.extra="{ item }">
        <div
          v-if="typeof item.extra === 'object' && item.extra"
          class="d-flex flex-column text-sm font-weight-normal text-body"
        >
          <div
            v-for="(val, key) in item.extra"
            :key="key"
            style="word-break: break-all"
            class="mb-1"
          >
            <template v-if="key === 're_clock_in'"
              >請假期間上班: {{ formatDateTime(val) }}</template
            >
            <template v-else-if="key === 're_clock_out'"
              >請假期間下班: {{ formatDateTime(val) }}</template
            >
          </div>
        </div>
        <div v-else class="d-flex align-center">
          <span
            class="text-sm font-weight-normal text-body"
            style="word-break: break-all"
          >
            {{ item.extra }}
          </span>
        </div>
      </template>

      <template v-slot:item.late="{ item }">
        <div v-if="editedItem && editedItem.id === item.id">
          <v-text-field
            v-model.number="editedItem.late"
            dense
            hide-details
            type="number"
          ></v-text-field>
        </div>
        <div v-else class="d-flex align-center">
          <span class="text-sm font-weight-normal text-body">
            {{ item.late !== null && item.late !== undefined ? item.late : 0 }}
          </span>
        </div>
      </template>

      <template v-slot:item.early="{ item }">
        <div v-if="editedItem && editedItem.id === item.id">
          <v-text-field
            v-model.number="editedItem.early"
            dense
            hide-details
            type="number"
          ></v-text-field>
        </div>
        <div v-else class="d-flex align-center">
          <span class="text-sm font-weight-normal text-body">
            {{
              item.early !== null && item.early !== undefined ? item.early : 0
            }}
          </span>
        </div>
      </template>

      <!-- actions -->
      <template v-slot:item.actions="{ item }">
        <div v-if="rootRole" class="d-flex align-center">
          <div v-if="editedItem && editedItem.id === item.id">
            <v-btn icon small color="red" @click="saveItem" class="mr-2">
              <v-icon size="20">mdi-content-save</v-icon>
            </v-btn>
            <v-btn icon small @click="cancelEdit">
              <v-icon size="20">mdi-close</v-icon>
            </v-btn>
          </div>
          <div v-else>
            <v-btn
              @click.stop="editItem(item)"
              icon
              elevation="0"
              :ripple="false"
              height="28"
              min-width="36"
              width="36"
              class="btn-ls me-2 my-2 rounded-sm"
              color="#67748e"
              title="編輯"
            >
              <v-icon size="18">mdi-pencil</v-icon>
            </v-btn>
            <v-btn
              @click.stop="viewHistory(item)"
              icon
              elevation="0"
              :ripple="false"
              height="28"
              min-width="36"
              width="36"
              class="btn-ls me-2 my-2 rounded-sm"
              color="#cb0c9f"
              title="歷史記錄"
            >
              <v-icon size="18">mdi-history</v-icon>
            </v-btn>
          </div>
        </div>
      </template>

      <template v-slot:body.append>
        <tr v-if="error">
          <td
            :colspan="headers.length"
            class="text-center red--text text-body-2"
          >
            {{ error }}
          </td>
        </tr>
        <tr v-else-if="!loading && !records.length">
          <td
            :colspan="headers.length"
            class="text-center grey--text text-body-2"
          >
            {{ selectedUserId ? "本月無資料" : "請選擇員工" }}
          </td>
        </tr>
      </template>
    </v-data-table>

    <info-dialog
      :visible.sync="infoDialog.visible"
      :title="infoDialog.title"
      :message="infoDialog.message"
      :yes-button-text="infoDialog.yesButtonText"
      :yes-callback="infoDialog.yesCallback"
    />

    <!-- History Dialog (Reused from Daily) -->
    <v-dialog v-model="historyDialog" max-width="900px" scrollable>
      <v-card class="card-shadow border-radius-xl">
        <div class="card-header-padding card-border-bottom">
          <span class="font-weight-bold text-h5 text-typo mb-0"
            >修改歷史記錄</span
          >
        </div>
        <v-card-text class="card-padding" style="max-height: 600px">
          <v-progress-linear
            v-if="historyLoading"
            indeterminate
            color="primary"
          />
          <div v-else-if="historyError" class="red--text text-center py-4">
            {{ historyError }}
          </div>
          <div
            v-else-if="!historyRecords.length"
            class="grey--text text-center py-4"
          >
            無修改記錄
          </div>
          <v-timeline v-else dense>
            <v-timeline-item
              v-for="record in historyRecords"
              :key="record.id"
              small
              color="primary"
            >
              <template v-slot:opposite>
                <span class="text-caption">
                  {{ formatDateTime(record.create_time) }}
                </span>
              </template>
              <v-card class="elevation-2">
                <v-card-text>
                  <div class="font-weight-bold mb-2">
                    修改人: {{ record.modifier ? record.modifier.name : "-" }}
                    <span
                      v-if="record.modifier && record.modifier.email"
                      class="text-caption grey--text"
                    >
                      ({{ record.modifier.email }})
                    </span>
                  </div>
                  <div class="text-caption grey--text mb-2">
                    {{ formatDateTime(record.create_time) }}
                  </div>
                  <v-divider class="my-2" />
                  <div class="text-body-2">
                    <div
                      v-for="(change, field) in record.changes"
                      :key="field"
                      class="mb-2"
                    >
                      <strong>{{ getFieldLabel(field) }}:</strong>
                      <div class="ml-4">
                        <div class="red--text text--lighten-1">
                          舊值: {{ formatFieldValue(field, change.old) }}
                        </div>
                        <div class="green--text text--darken-1">
                          新值: {{ formatFieldValue(field, change.new) }}
                        </div>
                      </div>
                    </div>
                  </div>
                </v-card-text>
              </v-card>
            </v-timeline-item>
          </v-timeline>
          <div v-if="historyMeta.totalPages > 1" class="text-center mt-4">
            <v-pagination
              v-model="historyPage"
              :length="historyMeta.totalPages"
              @input="loadHistory"
              circle
              color="primary"
            />
          </div>
        </v-card-text>
        <v-card-actions class="card-padding d-flex justify-end">
          <v-btn
            @click="closeHistory"
            elevation="0"
            :ripple="false"
            height="43"
            class="
              font-weight-bold
              text-capitalize
              btn-ls btn-secondary
              bg-gradient-light
              py-3
              px-6
            "
            >關閉</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import { mapGetters } from "vuex";
import DateTimePicker from "@/components/Common/DateTimePicker.vue";
import InfoDialog from "@/components/Common/InfoDialog.vue";
import { formatDateTime as formatDateTimeUtil } from "@/utils/time";
import { LEAVE_TYPE } from "@/constants";

export default {
  name: "MonthlyClockRecords",
  components: {
    DateTimePicker,
    InfoDialog,
  },
  props: {
    targetUserId: {
      type: Number,
      default: null,
    },
    hideUserSelect: {
      type: Boolean,
      default: false,
    },
    hideTitle: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth() + 1;
    return {
      records: [],
      loading: false,
      loadingUsers: false,
      error: null,
      selectedYear: currentYear,
      selectedMonth: currentMonth,
      yearOptions: Array.from({ length: 10 }, (_, i) => currentYear - 5 + i),
      monthOptions: Array.from({ length: 12 }, (_, i) => i + 1),
      users: [],
      selectedUserId: this.targetUserId || null,

      currentPage: 1,
      pageCount: 0,
      itemsPerPage: 31, // Default to approx 1 month
      totalItems: 0,

      editedItem: null,
      clockInActualDate: null,
      clockInActualTime: null,
      clockOutActualDate: null,
      clockOutActualTime: null,
      lunchBreakStartActualDate: null,
      lunchBreakStartActualTime: null,
      lunchBreakEndActualDate: null,
      lunchBreakEndActualTime: null,

      activeGPSKey: null,

      // History
      historyDialog: false,
      historyLoading: false,
      historyError: null,
      historyRecords: [],
      historyMeta: {
        page: 1,
        limit: 10,
        total: 0,
        totalPages: 0,
      },
      historyPage: 1,
      currentHistoryRecordId: null,

      infoDialog: {
        visible: false,
        title: "",
        message: "",
        yesButtonText: "確定",
        yesCallback: null,
      },

      headers: [
        {
          text: "日期",
          value: "date",
          sortable: true,
          class: "text-secondary font-weight-bolder opacity-7 black--text",
        },
        // Removed User Name column as it's filtered by user
        {
          text: "實際上班",
          value: "clock_in_actual",
          sortable: false,
          class: "text-secondary font-weight-bolder opacity-7 black--text",
        },
        {
          text: "實際下班",
          value: "clock_out_actual",
          sortable: false,
          class: "text-secondary font-weight-bolder opacity-7 black--text",
        },
        {
          text: "午休實際開始",
          value: "lunch_break_start_actual",
          sortable: false,
          class: "text-secondary font-weight-bolder opacity-7 black--text",
        },
        {
          text: "午休實際結束",
          value: "lunch_break_end_actual",
          sortable: false,
          class: "text-secondary font-weight-bolder opacity-7 black--text",
        },
        {
          text: "加班",
          value: "overtime",
          sortable: false,
          class: "text-secondary font-weight-bolder opacity-7 black--text",
        },
        {
          text: "加班申請",
          value: "overtime_record_id",
          sortable: false,
          class: "text-secondary font-weight-bolder opacity-7 black--text",
        },
        {
          text: "請假紀錄",
          value: "leave_record_ids",
          sortable: false,
          class: "text-secondary font-weight-bolder opacity-7 black--text",
        },
        // extra
        {
          text: "備註",
          value: "extra",
          sortable: false,
          class: "text-secondary font-weight-bolder opacity-7 black--text",
        },
        {
          text: "遲到",
          value: "late",
          sortable: false,
          class: "text-secondary font-weight-bolder opacity-7 black--text",
        },
        {
          text: "早退",
          value: "early",
          sortable: false,
          class: "text-secondary font-weight-bolder opacity-7 black--text",
        },
        {
          text: "",
          value: "actions",
          sortable: false,
          class: "text-secondary font-weight-bolder opacity-7 black--text",
        },
      ],
    };
  },
  computed: {
    ...mapGetters("company", ["selectedCompany"]),
    ...mapGetters("user", ["rootRole"]),
  },
  watch: {
    targetUserId: {
      handler(val) {
        if (val) {
          this.selectedUserId = val;
          this.fetchAttendanceRecords();
        }
      },
      immediate: true,
    },
    selectedCompany: {
      handler(newVal) {
        if (newVal) {
          if (!this.targetUserId) {
            this.selectedUserId = null;
            this.records = [];
          }
          this.fetchUsers();
        }
      },
      deep: true,
      immediate: true,
    },
    // If we want to auto-fetch when year/month changes, we can,
    // but only if user is selected.
  },
  mounted() {
    // Initialization if needed
  },
  methods: {
    getRecordDate(item) {
      // Try to identify date from record
      if (item.clock_in_time) return item.clock_in_time.split("T")[0];
      if (item.clock_in_actual) return item.clock_in_actual.split("T")[0];
      // If nothing else, we might need the date field from backend if it exists.
      // Assuming the backend returns records sorted by time or something.
      // For now let's hope clock_in_time (scheduled) exists for every day record
      return "-";
    },
    getRecordWeekDay(item) {
      const date = new Date(item.clock_date);
      const days = ["日", "一", "二", "三", "四", "五", "六"];
      return `(${days[date.getDay()]})`;
    },
    formatDateTime(value) {
      return formatDateTimeUtil(value);
    },
    getLeaveTypeName(typeId) {
      return LEAVE_TYPE[typeId] || `假別${typeId}`;
    },
    async fetchUsers() {
      this.loadingUsers = true;
      try {
        const companyId = this.selectedCompany ? this.selectedCompany.id : null;
        if (!companyId) {
          this.users = [];
          return;
        }
        const allUsers = [];
        const seenUserIds = new Set();
        let page = 1;
        const limit = 100;
        let hasNextPage = true;

        // /api/users is paginated. Keep requesting next page so autocomplete
        // can search/select the full employee list.
        while (hasNextPage) {
          const params = new URLSearchParams({
            company_id: companyId,
            page: String(page),
            limit: String(limit),
          });
          const response = await this.$axios.get(`/api/users?${params}`);
          const payload = response.data;
          const pageUsers = Array.isArray(payload)
            ? payload
            : Array.isArray(payload.data)
            ? payload.data
            : [];

          pageUsers.forEach((user) => {
            if (!user || user.id == null || seenUserIds.has(user.id)) return;
            seenUserIds.add(user.id);
            allUsers.push(user);
          });

          const pagination = payload?.pagination || null;
          hasNextPage = Boolean(
            pagination?.hasNextPage || pagination?.nextPage
          );
          page = pagination?.nextPage || page + 1;
        }

        this.users = allUsers;
      } catch (error) {
        console.error("Fetch users error:", error);
      } finally {
        this.loadingUsers = false;
      }
    },
    onUserChange() {
      this.currentPage = 1;
      this.fetchAttendanceRecords();
    },
    onDateChange() {
      if (this.selectedUserId) {
        this.currentPage = 1;
        this.fetchAttendanceRecords();
      }
    },
    async fetchAttendanceRecords() {
      if (!this.selectedUserId) {
        return;
      }
      this.loading = true;
      this.error = null;

      // Calculate start and end date
      const startDate = new Date(this.selectedYear, this.selectedMonth - 1, 1);
      const endDate = new Date(this.selectedYear, this.selectedMonth, 0); // Last day of month

      const formatDate = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
      };

      const queryParams = new URLSearchParams({
        page: this.currentPage,
        limit: this.itemsPerPage,
        start_date: formatDate(startDate),
        end_date: formatDate(endDate),
        company_id: this.selectedCompany.id,
        user_id: this.selectedUserId,
      });

      try {
        const response = await this.$axios.get(
          `/api/clock-records?${queryParams}`
        );

        let newRecords = response.data.rows || [];
        // Optional: Sorting by date if not already sorted?
        // Usually backend sorts.

        this.records = newRecords;
        this.pageCount = response.data.pagination.totalPages;
        this.totalItems = response.data.pagination
          ? response.data.pagination.totalItems
          : 0;
      } catch (error) {
        this.records = [];
        this.error =
          error?.response?.data?.message ||
          error.message ||
          "取得資料時發生錯誤";
      } finally {
        this.loading = false;
      }
    },

    handleItemsPerPageChange(itemsPerPage) {
      this.itemsPerPage = itemsPerPage;
      this.currentPage = 1;
      this.fetchAttendanceRecords();
    },
    // Edit Related Methods (Copied from DailyClockRecords)
    editItem(item) {
      this.editedItem = Object.assign({}, item);
      this.initializeDateTimePickers();
    },
    initializeDateTimePickers() {
      if (!this.editedItem) return;
      this.parseDateTime(
        "clock_in_actual",
        "clockInActualDate",
        "clockInActualTime",
        this.editedItem.clock_date
      );
      this.parseDateTime(
        "clock_out_actual",
        "clockOutActualDate",
        "clockOutActualTime",
        this.editedItem.clock_date
      );
      this.parseDateTime(
        "lunch_break_start_actual",
        "lunchBreakStartActualDate",
        "lunchBreakStartActualTime",
        this.editedItem.clock_date
      );
      this.parseDateTime(
        "lunch_break_end_actual",
        "lunchBreakEndActualDate",
        "lunchBreakEndActualTime",
        this.editedItem.clock_date
      );
    },
    parseDateTime(field, dateField, timeField, defaultDate = null) {
      const value = this.editedItem[field];
      if (value) {
        const dt = new Date(value);
        if (!Number.isNaN(dt.getTime())) {
          this[dateField] = dt.toISOString().split("T")[0];
          this[timeField] = dt.toTimeString().split(" ")[0].substring(0, 5);
        }
      } else {
        const now = new Date();
        this[dateField] = defaultDate || now.toISOString().split("T")[0];
        this[timeField] = now.toTimeString().split(" ")[0].substring(0, 5);
      }
    },
    combineDateTimeToISO(date, time) {
      if (!date) return null;
      const timeStr = time || "00:00";
      return new Date(`${date}T${timeStr}:00+08:00`).toISOString();
    },
    updateClockInActual() {
      this.editedItem.clock_in_actual = this.combineDateTimeToISO(
        this.clockInActualDate,
        this.clockInActualTime
      );
    },
    updateClockOutActual() {
      this.editedItem.clock_out_actual = this.combineDateTimeToISO(
        this.clockOutActualDate,
        this.clockOutActualTime
      );
    },
    updateLunchBreakStartActual() {
      this.editedItem.lunch_break_start_actual = this.combineDateTimeToISO(
        this.lunchBreakStartActualDate,
        this.lunchBreakStartActualTime
      );
    },
    updateLunchBreakEndActual() {
      this.editedItem.lunch_break_end_actual = this.combineDateTimeToISO(
        this.lunchBreakEndActualDate,
        this.lunchBreakEndActualTime
      );
    },
    cancelEdit() {
      this.editedItem = null;
    },
    async saveItem() {
      if (!this.editedItem) return;

      try {
        const payload = {
          clock_in_actual: this.editedItem.clock_in_actual,
          clock_out_actual: this.editedItem.clock_out_actual,
          lunch_break_start_actual: this.editedItem.lunch_break_start_actual,
          lunch_break_end_actual: this.editedItem.lunch_break_end_actual,
          overtime: this.editedItem.overtime,
          // Other fields if needed
          late: this.editedItem.late,
          early: this.editedItem.early,
        };

        await this.$axios.put(
          `/api/clock-records/${this.editedItem.id}`,
          payload
        );

        const index = this.records.findIndex(
          (r) => r.id === this.editedItem.id
        );
        if (index !== -1) {
          this.records.splice(index, 1, Object.assign({}, this.editedItem));
        }

        this.editedItem = null;
        this.$emit("success", "更新成功"); // Optional
      } catch (error) {
        const errMsg =
          error?.response?.data?.message || error.message || "更新失敗";
        this.infoDialog = {
          visible: true,
          title: "更新失敗",
          message: errMsg,
        };
      }
    },

    // History methods (Copied)
    async viewHistory(item) {
      this.currentHistoryRecordId = item.id;
      this.historyPage = 1;
      this.historyDialog = true;
      await this.loadHistory();
    },
    async loadHistory() {
      if (!this.currentHistoryRecordId) return;
      this.historyLoading = true;
      this.historyError = null;
      try {
        const queryParams = new URLSearchParams({
          page: this.historyPage,
          limit: this.historyMeta.limit,
        });
        const response = await this.$axios.get(
          `/api/history/clock-records/${this.currentHistoryRecordId}?${queryParams}`
        );
        this.historyRecords = response.data.data || [];
        this.historyMeta = response.data.meta || {
          page: 1,
          limit: 10,
          total: 0,
          totalPages: 0,
        };
      } catch (error) {
        this.historyError =
          error?.response?.data?.message || "無法載入歷史記錄";
        this.historyRecords = [];
      } finally {
        this.historyLoading = false;
      }
    },
    closeHistory() {
      this.historyDialog = false;
      this.currentHistoryRecordId = null;
      this.historyRecords = [];
      this.historyPage = 1;
    },
    getFieldLabel(field) {
      const labels = {
        clock_in_actual: "實際上班",
        clock_out_actual: "實際下班",
        lunch_break: "午休",
        lunch_break_start_actual: "午休實際開始",
        lunch_break_end_actual: "午休實際結束",
        overtime: "加班",
        overtime_apply_id: "加班申請",
        late: "遲到",
        early: "早退",
        clock_in_time: "排定上班",
        clock_out_time: "排定下班",
        lunch_break_start: "午休排定開始",
        lunch_break_end: "午休排定結束",
      };
      return labels[field] || field;
    },
    formatFieldValue(field, value) {
      if (value === null || value === undefined) return "-";

      const dateTimeFields = [
        "clock_in_actual",
        "clock_out_actual",
        "lunch_break_start_actual",
        "lunch_break_end_actual",
        "clock_in_time",
        "clock_out_time",
        "lunch_break_start",
        "lunch_break_end",
      ];
      if (dateTimeFields.includes(field)) {
        return this.formatDateTime(value);
      }
      if (field === "lunch_break") return value ? "是" : "否";
      if (field === "overtime")
        return value === true ? "是" : value === false ? "否" : "-";
      if (typeof value === "number") return value.toString();
      return value;
    },
    isValidGPS(gps) {
      if (!gps || typeof gps !== "string") return false;
      return gps.includes(",");
    },
    openGPSInGoogleMaps(gps) {
      if (!this.isValidGPS(gps)) return;
      const [lat, lon] = gps.split(",");
      const url = `https://www.google.com/maps/search/?api=1&query=${lat.trim()},${lon.trim()}`;
      window.open(url, "_blank");
    },
  },
};
</script>

<style scoped>
.table >>> thead th {
  white-space: nowrap;
  font-size: 1rem !important;
  font-weight: 700 !important;
  text-transform: uppercase;
  letter-spacing: 0.025rem;
  color: #8392ab;
  opacity: 0.7;
  padding: 0.75rem 1.5rem;
  border-bottom: 0.0625rem solid #e9ecef;
}
.table >>> tbody tr:last-child td {
  border-bottom: 0;
}
.table >>> tbody td {
  padding: 0.75rem 1.5rem;
  border-bottom: 0.0625rem solid #e9ecef;
}
.table >>> tbody tr:hover {
  background: #f8f9fa;
}
</style>
